/**
Event Bus - Observable cross-module communication system.
Provides typed, traceable events with no direct import coupling between modules.
*/

/** Captured local + global variable scope at a debugger breakpoint. */
export interface RuntimeDebugScope {
  locals:  Record<string, unknown>;
  globals: Record<string, unknown>;
}

export type NuvraEvent =

| { type: "runtime:init" }
| { type: "runtime:ready" }
| { type: "runtime:shutdown" }

| { type: "module:registered"; moduleId: string }
| { type: "module:initialized"; moduleId: string }
| { type: "module:error"; moduleId: string; error: string }

| { type: "state:changed"; key: string }
| { type: "state:saved"; projectId: string }
| { type: "state:loaded"; projectId: string }
| { type: "state:corrupted"; projectId: string; reason: string }

/* =========================================================
🔥 ADDED - EDITOR STATE EVENTS (FIXES YOUR ERROR)
========================================================= */
| { type: "editor:updated"; projectId: string; fileCount: number }
| { type: "editor:stream"; projectId: string; content: string }
| { type: "editor:cursor"; file: string; line: number; column: number }
| { type: "editor:open-file"; file: string; line?: number; column?: number }
| { type: "file:selected"; file: string }

| { type: "page:created"; pageId: string }
| { type: "page:deleted"; pageId: string }
| { type: "page:switched"; pageId: string }

| { type: "editor:action"; action: string }

| { type: "persistence:save"; projectId: string }
| { type: "persistence:load"; projectId: string }
| { type: "persistence:error"; reason: string }

| { type: "crash:detected"; containerId: string }
| { type: "crash:recovered"; containerId: string }

| { type: "build:started";   projectId: string; buildId?: string }
| { type: "build:completed"; projectId: string; buildId?: string }
| { type: "build:failed";    projectId: string; buildId?: string; reason: string }

| { type: "deploy:started";   projectId: string }
| { type: "deploy:completed"; projectId: string; url: string }
| { type: "deploy:failed";    projectId: string; reason: string }

| { type: "pipeline:stage_start";    buildId: string; projectId: string; stage: string }
| { type: "pipeline:stage_complete"; buildId: string; projectId: string; stage: string; durationMs: number }
| { type: "pipeline:stage_failed";   buildId: string; projectId: string; stage: string; error: string; durationMs: number }
| { type: "pipeline:stage_retry";    buildId: string; projectId: string; stage: string; attempt: number; maxAttempts: number; delayMs: number; error: string }

| { type: "factory:started"; runId: string }
| { type: "factory:stage_start"; runId: string; stage: string; label: string }
| { type: "factory:stage_complete"; runId: string; stage: string; durationMs: number }
| { type: "factory:stage_failed"; runId: string; stage: string; error: string }
| { type: "factory:completed"; runId: string; status: string }
| { type: "factory:deployed"; appName: string; url: string; environment: string }

| { type: "recovery:failure_detected"; component: string; error: string }
| { type: "recovery:component_restored"; component: string }

/* =========================================================
   MARKETPLACE EVENTS
   ========================================================= */
| { type: "marketplace:listing_created";   userId: string; listingId: string; traceId: string }
| { type: "marketplace:listing_updated";   userId: string; listingId: string; traceId: string; fields: string[] }
| { type: "marketplace:listing_published"; userId: string; listingId: string; traceId: string }
| { type: "marketplace:listing_deleted";   userId: string; listingId: string; traceId: string; previousStatus: string }

/* =========================================================
   PURCHASE EVENTS
   ========================================================= */
| { type: "marketplace:purchase_completed";      buyerId: string; sellerId: string; listingId: string; purchaseId: string; amountCents: number; traceId: string }
| { type: "marketplace:purchase_free_completed"; buyerId: string; sellerId: string; listingId: string; purchaseId: string; traceId: string }
| { type: "marketplace:purchase_already_exists"; buyerId: string; listingId: string; purchaseId: string; traceId: string }

/* =========================================================
   REVIEW EVENTS
   ========================================================= */
| { type: "marketplace:review_created"; reviewerId: string; sellerId: string; listingId: string; reviewId: string; rating: number; traceId: string }

/* =========================================================
   PAYOUT EVENTS
   ========================================================= */
| { type: "marketplace:payout_requested";  sellerId: string; payoutId: string; amountCents: number; currency: string; traceId: string }
| { type: "marketplace:payout_processing"; sellerId: string; payoutId: string; traceId: string }
| { type: "marketplace:payout_completed";  sellerId: string; payoutId: string; amountCents: number; externalRef: string; traceId: string }
| { type: "marketplace:payout_failed";     sellerId: string; payoutId: string; reason: string; traceId: string }

/* =========================================================
   AFFILIATE EVENTS
   ========================================================= */
| { type: "marketplace:affiliate_account_created";    affiliateId: string; traceId: string }
| { type: "marketplace:affiliate_link_generated";     affiliateId: string; linkId: string; code: string; traceId: string }
| { type: "marketplace:affiliate_commission_created"; affiliateId: string; commissionId: string; purchaseId: string; commissionCents: number; traceId: string }

/* =========================================================
   TEMPLATE EVENTS
   ========================================================= */
| { type: "template:created";         userId: string; templateId: string; traceId: string }
| { type: "template:published";       userId: string; templateId: string; traceId: string }
| { type: "template:installed";       userId: string; templateId: string; projectId: string; traceId: string }
| { type: "template:archived";        userId: string; templateId: string; traceId: string }
| { type: "template:install_started"; userId: string; templateId: string; installId: string; installMode: string; traceId: string }
| { type: "template:install_failed";  userId: string; templateId: string; reason: string; code: string; traceId: string }

/* =========================================================
   TEMPLATE VERSION EVENTS
   ========================================================= */
| { type: "template:version_created";    userId: string; templateId: string; versionId: string; version: string; traceId: string }
| { type: "template:version_released";   userId: string; templateId: string; versionId: string; version: string; traceId: string }
| { type: "template:version_deprecated"; userId: string; templateId: string; versionId: string; version: string; reason: string; traceId: string }
| { type: "template:version_rolledback"; userId: string; templateId: string; toVersionId: string; toVersion: string; fromVersionId: string; fromVersion: string; traceId: string }

/* =========================================================
   TEMPLATE FORK EVENTS
   ========================================================= */
| { type: "template:forked";               userId: string; forkTemplateId: string; parentTemplateId: string; rootAncestorId: string; forkDepth: number; forkedFromVersionId: string | null; traceId: string }
| { type: "template:fork_depth_limit_reached"; userId: string; templateId: string; attemptedDepth: number; maxDepth: number; traceId: string }
| { type: "template:fork_storm_detected";  userId: string; templateId: string; directForkCount: number; limit: number; traceId: string }

/* =========================================================
   TEMPLATE AI GENERATION EVENTS
   ========================================================= */
| { type: "template:ai_generation_started";   userId: string; generationId: string; mode: string; traceId: string }
| { type: "template:ai_generation_completed"; userId: string; generationId: string; templateId: string; traceId: string }
| { type: "template:ai_generation_failed";    userId: string; generationId: string; reason: string; code: string; phase: number; traceId: string }
| { type: "template:ai_review_flagged";       userId: string; generationId: string; templateId: string; traceId: string }

/* =========================================================
   COMPATIBILITY EVENTS
   ========================================================= */
| { type: "template:compatibility_checked"; templateId: string; versionId: string | null; grade: string; installable: boolean; traceId: string }
| { type: "template:upgrade_blocked";       templateId: string; fromVersionId: string; toVersionId: string; reason: string; traceId: string }
| { type: "template:migration_required";    templateId: string; fromVersionId: string; toVersionId: string; traceId: string }

/* =========================================================
   ENCRYPTION EVENTS
   ========================================================= */
| { type: "encryption:rotation_required"; context: string; payloadKeyVersion: string; activeKeyVersion: string; traceId: string }

/* =========================================================
   LICENSE EVENTS
   ========================================================= */
| { type: "license:issued";    licenseId: string; subjectId: string; resourceId: string; licenseType: string; traceId: string }
| { type: "license:validated"; licenseId: string; subjectId: string; validationMode: string; traceId: string }
| { type: "license:rejected";  licenseId: string; subjectId: string; reason: string; traceId: string }
| { type: "license:expired";   licenseId: string; subjectId: string; expiredAt: string; traceId: string }

/* =========================================================
   WATERMARK EVENTS
   ========================================================= */
| { type: "watermark:embedded"; watermarkId: string; resourceId: string; authorId: string; mode: string; watermarkType: string; traceId: string }
| { type: "watermark:verified"; watermarkId: string; resourceId: string; traceId: string }
| { type: "watermark:tampered"; watermarkId: string; resourceId: string; storedHash: string; computedHash: string; traceId: string }

/* =========================================================
   SECURITY AUDIT EVENTS
   ========================================================= */
| { type: "audit:record_created"; recordId: string; sequenceNumber: number; eventType: string; severity: string; traceId: string }
| { type: "audit:chain_verified"; chainLength: number; valid: boolean; traceId: string }
| { type: "audit:chain_broken";   breakAtSequence: number; traceId: string }

/* =========================================================
   MARKETPLACE COPILOT EVENTS
   ========================================================= */
| { type: "copilot:discover_started";         userId: string | null; traceId: string }
| { type: "copilot:discover_completed";       userId: string | null; resultCount: number; intent: string; traceId: string }
| { type: "copilot:recommend_started";        userId: string | null; traceId: string }
| { type: "copilot:recommend_completed";      userId: string | null; resultCount: number; traceId: string }
| { type: "copilot:generation_guidance_built"; userId: string | null; intent: string; traceId: string }
| { type: "copilot:budget_exhausted";         operation: string; traceId: string }
| { type: "copilot:fallback_used";            operation: string; reason: string; traceId: string }

/* =========================================================
   PERSISTENCE SYNC EVENTS
   ========================================================= */
| { type: "persistence:local_miss";           projectId: string }
| { type: "persistence:queue_enqueued";       projectId: string; sequenceNum: number }
| { type: "persistence:sync_start";           projectId: string; sequenceNum: number }
| { type: "persistence:cloud_synced";         projectId: string; sequenceNum: number; durationMs: number }
| { type: "persistence:sync_failed";          projectId: string; sequenceNum: number; error: string; attempt: number; nextRetryMs: number }
| { type: "persistence:sync_exhausted";       projectId: string; sequenceNum: number; totalAttempts: number }
| { type: "persistence:journal_appended";     projectId: string; sequenceNum: number }
| { type: "persistence:journal_replaying";    projectId: string; pendingCount: number }
| { type: "persistence:journal_replayed";     projectId: string; replayedCount: number }
| { type: "persistence:reconciliation_needed";   projectId: string; localSeq: number; remoteSeq: number }
| { type: "persistence:reconciliation_resolved"; projectId: string; winner: "local" | "remote"; strategyId: string }
| { type: "persistence:ledger_updated";       projectId: string; status: string }
| { type: "persistence:loaded_from_local";    projectId: string; sequenceNum: number }
| { type: "persistence:loaded_from_cloud";    projectId: string; sequenceNum: number }
| { type: "persistence:cloud_load_failed";    projectId: string; reason: string }
| { type: "persistence:cloud_restore";        projectId: string; snapshot: unknown; sequenceNum: number }

/* =========================================================
   STATE MIGRATION EVENTS
   Emitted by migrationRunner.ts during schema version upgrades.
   Observed by monitoring/observability layers.
   ========================================================= */
| { type: "migration:started";          storageKey: string; fromVersion: number; toVersion: number }
| { type: "migration:step";             storageKey: string; stepIndex: number; stepCount: number; fromVersion: number; toVersion: number }
| { type: "migration:completed";        storageKey: string; fromVersion: number; toVersion: number; durationMs: number }
| { type: "migration:failed";           storageKey: string; fromVersion: number; toVersion: number; reason: string }
| { type: "migration:skipped";          storageKey: string; reason: string }
| { type: "migration:backup_saved";     storageKey: string; backupKey: string }
| { type: "migration:rollback_applied"; storageKey: string; backupKey: string }

/* =========================================================
   ACU COMPUTE EVENTS
   Emitted by computeStore → consumed by acuPersistenceBridge.
   Local writes are synchronous; cloud sync is asynchronous.
   ========================================================= */
| { type: "compute:acu_consumed";   projectId?: string; operationId: string; operation: string; acuConsumed: number; computeMode: string; timestamp: number }
| { type: "compute:usage_synced";   userId: string; sequenceNum: number; durationMs: number }
| { type: "compute:sync_failed";    userId: string; sequenceNum: number; error: string; attempt: number; nextRetryMs: number }
| { type: "compute:sync_exhausted"; userId: string; sequenceNum: number; totalAttempts: number }

/* =========================================================
   MODULE HEALTH EVENTS  (Subsystem 6)
   Emitted by ModuleHealthMonitor during periodic assessments.
   Consumed by: platformHealthMonitor, observabilityEngine, AI systems.

   GOVERNANCE RULE: status values here must match ModuleHealthStatusLevel
   in coreRuntime.ts. "unknown" is the truthful default - never "healthy"
   without active verification. Fabricated certainty is forbidden.
   ========================================================= */
| {
    type:          "module:health_changed";
    moduleId:      string;
    /** Previous status - null if first assessment. */
    previous:      "healthy" | "degraded" | "unhealthy" | "unknown" | null;
    current:       "healthy" | "degraded" | "unhealthy" | "unknown";
    detail?:       string;
    /** moduleId of upstream module that caused a cascade, if applicable. */
    cascadedFrom?: string;
  }
| {
    type:      "module:health_checked";
    moduleId:  string;
    status:    "healthy" | "degraded" | "unhealthy" | "unknown";
    detail?:   string;
    checkedAt: number;
  }
| {
    type:             "runtime:health_assessed";
    overall:          "healthy" | "degraded" | "unhealthy" | "unknown";
    moduleCount:      number;
    unhealthyModules: string[];
    degradedModules:  string[];
  }

/* =========================================================
   HTTP CIRCUIT BREAKER EVENTS  (Subsystem 6)
   Emitted by HttpCircuitBreaker on state transitions.
   Consumed by: platformHealthMonitor, storageAdapter, acuPersistenceBridge.

   GOVERNANCE RULE: circuit state must be truthfully reported.
   Open circuit = service unavailable. Do not report healthy when circuit open.
   ========================================================= */
| {
    type:         "circuit:opened";
    serviceId:    string;
    failureCount: number;
    /** Epoch ms when the circuit will attempt half-open probe. */
    resetAt:      number;
  }
| {
    type:      "circuit:half_open";
    serviceId: string;
  }
| {
    type:            "circuit:closed";
    serviceId:       string;
    /** true if closed after a successful half-open probe (recovery). */
    probeSucceeded:  boolean;
  }
| {
    type:      "circuit:rejected";
    serviceId: string;
    reason:    "circuit_open";
  }

/* =========================================================
   SELF-HEALING EVENTS  (Phase 2-2.5 Governance Containment)

   Emitted by the self-healing orchestrator during assessment cycles.
   Phase 3C: SELF_HEALING_EXECUTION_ENABLED = true - repair execution is now active.
   All mutations route through mutateSchema("system:repair") → ImmutableHistory.

   GOVERNANCE RULE: selfHealing:repair_proposed is emitted for proposals only
   (when execution is suspended or blocked). selfHealing:repair_applied is emitted
   when a repair is successfully executed. Mutations never bypass canonical authority.

   Detection (runtimeMonitor) and planning (fixPlanner) remain active.
   All operations are observable via eventBus and ImmutableHistory.
   ========================================================= */
| {
    type:             "selfHealing:cycle_started";
    /** Number of actionable issues detected at cycle start. */
    issueCount:       number;
    projectId:        string | null;
    /** Mirrors SELF_HEALING_EXECUTION_ENABLED - always false during Phase 2-2.5. */
    executionEnabled: boolean;
  }
| {
    type:               "selfHealing:cycle_completed";
    totalIssues:        number;
    /** Issues for which a repair was proposed (execution suspended). */
    proposed:           number;
    /** Issues blocked due to prior failure threshold. */
    blocked:            number;
    healthScore:        number;
    durationMs:         number;
    /** true while SELF_HEALING_EXECUTION_ENABLED = false. */
    executionSuspended: boolean;
  }
| {
    type:        "selfHealing:issue_detected";
    issueId:     string;
    category:    string;
    severity:    string;
    title:       string;
    autoFixable: boolean;
  }
| {
    type:             "selfHealing:repair_proposed";
    issueId:          string;
    category:         string;
    severity:         string;
    title:            string;
    planSteps:        number;
    estimatedImpact:  string;
    requiresReview:   boolean;
    /**
     * Literal true - this proposal was not executed.
     * Cannot be false while SELF_HEALING_EXECUTION_ENABLED = false.
     */
    executionBlocked: true;
  }
| {
    type:   "selfHealing:execution_blocked";
    /** SUSPENSION_REASON text - explains why execution is suspended. */
    reason: string;
  }
| {
    type:   "selfHealing:suspended";
    /** SUSPENSION_REASON text - emitted once per cycle start and at runner start. */
    reason: string;
  }
/**
 * Phase 3C: emitted by repairEngine after a repair is successfully applied.
 * fingerprintBefore !== fingerprintAfter guarantees canonical state advanced.
 */
| {
    type:              "selfHealing:repair_applied";
    projectId:         string;
    issueId:           string;
    stepsApplied:      number;
    fingerprintBefore: string;
    fingerprintAfter:  string;
  }
/**
 * Phase 3C: emitted by repairEngine after a failed repair rolls back to pre-repair state.
 * canonicalRestored:true means bridge canonical schema was also restored (lossless rollback).
 */
| {
    type:              "selfHealing:repair_rolled_back";
    projectId:         string;
    issueId:           string;
    canonicalRestored: boolean;
  }

// ─── Canonical Persistence Events ───

/**
 * Emitted by projectStore.loadProject() / restoreFromCloud() when a v2 payload
 * contains a canonicalSchema field. schemaBridge pre-populates currentSchema from
 * this event, allowing initSchemaFromStore() to skip store reconstruction.
 */
| {
    type:      "persistence:canonical_available";
    projectId: string;
    schema:    import("@/core/schema/nuvraSchema").NuvraSchema;
  }

/**
 * Emitted by schemaBridge when the canonical schema from persistence passes
 * fingerprint + structural validation. Confirms canonical-first restore succeeded.
 */
| {
    type:        "persistence:canonical_restored";
    projectId:   string;
    fingerprint: string;
  }

/**
 * Emitted by schemaBridge when the canonical schema from persistence fails
 * validation (fingerprint mismatch or structural errors). initSchemaFromStore()
 * will fall back to store reconstruction.
 */
| {
    type:      "persistence:canonical_restore_failed";
    projectId: string;
    reason:    string;
  }

/**
 * Emitted by persistenceBridge.onStateSaved() when canonical schema was included
 * in the save payload. Confirms canonical data is being persisted locally.
 */
| {
    type:        "persistence:canonical_saved";
    projectId:   string;
    fingerprint: string;
  }

/**
 * Emitted by persistenceBridge.syncEntry() after a successful Supabase write
 * that included canonical_schema. Confirms canonical schema reached cloud storage.
 */
| {
    type:        "persistence:cloud_canonical_saved";
    projectId:   string;
    fingerprint: string;
  }

/**
 * Emitted by persistenceBridge.onLocalMiss() when the cloud payload returned
 * by SupabaseAdapter.read() includes a non-null canonical_schema.
 * Fires before persistence:cloud_restore - signals canonical data is available.
 */
| {
    type:        "persistence:cloud_canonical_restored";
    projectId:   string;
    fingerprint: string;
  }

// ─── Schema Bridge Events ───

/**
 * Emitted by mutateSchema() for every bridge-managed canonical mutation.
 * Carries full fingerprint context for downstream consumers.
 */
| {
    type:               "schema:mutation";
    mutationType:       string;
    fingerprint:        string;
    previousFingerprint: string;
    source:             "user" | "ai" | "system";
    projectId:          string;
    description?:       string;
  }

/**
 * Emitted after _setFromCanonical() atomically writes all canonical
 * projections to the projectStore in a single state transition.
 */
| {
    type:          "schema:synced_to_store";
    projectId:     string;
    fingerprint:   string;
    fieldsUpdated: string[];
  }

/**
 * Emitted when the canonical schema is first established for a project
 * (either from store reconstruction or canonical load).
 */
| {
    type:        "schema:initialized";
    projectId:   string;
    fingerprint: string;
    /** true = rebuilt from store projection; false = loaded from canonical source */
    fromStore:   boolean;
  }

/**
 * Emitted when the canonical schema diverges from the store:
 * - "undo" / "redo"       - store restored to snapshot, canonical is stale
 * - "cloud_restore"       - store replaced by cloud payload, canonical is stale
 * - "direct_store_write"  - store was mutated directly, bypassing the bridge
 * These events trigger canonical schema rebuild (undo/redo/cloud_restore)
 * or are tracked as bypass metrics (direct_store_write).
 */
| {
    type:       "schema:staleness_detected";
    reason:     "undo" | "redo" | "direct_store_write" | "cloud_restore" | "unknown";
    projectId?: string;
  }

/**
 * Emitted by undo() when the undo target snapshot carries a NuvraSchema.
 * The bridge handles this BEFORE schema:staleness_detected so it can pre-populate
 * currentSchema and skip the lossy store-reconstruction rebuild.
 */
| {
    type:             "schema:undo_canonical_available";
    projectId:        string;
    schema:           import("@/core/schema/nuvraSchema").NuvraSchema;
    /** Set by repairEngine rollback - bridge increments _repairRollbacks instead of _canonicalUndoRestores. */
    isRepairRollback?: true;
    /** Set by regenerationLock rollback - bridge increments _regenerationRollbacks instead of _canonicalUndoRestores. */
    isRegenerationRollback?: true;
  }

/**
 * Emitted by redo() when the redo target snapshot carries a NuvraSchema.
 * Same pre-population pattern as schema:undo_canonical_available.
 */
| {
    type:      "schema:redo_canonical_available";
    projectId: string;
    schema:    import("@/core/schema/nuvraSchema").NuvraSchema;
  }

/**
 * Emitted by the bridge after successfully restoring a canonical NuvraSchema
 * from an undo snapshot. Fingerprint-validated before acceptance.
 */
| {
    type:        "schema:canonical_undo_applied";
    projectId:   string;
    fingerprint: string;
  }

/**
 * Emitted by the bridge after successfully restoring a canonical NuvraSchema
 * from a redo snapshot. Fingerprint-validated before acceptance.
 */
| {
    type:        "schema:canonical_redo_applied";
    projectId:   string;
    fingerprint: string;
  }

/* =========================================================
   REGENERATION LOCK EVENTS  (Phase 4A)

   Emitted by regenerationLock.ts during lock lifecycle transitions.
   Consumed by: schemaBridge (enforcement), observabilityEngine, AI systems.

   GOVERNANCE RULE: lock_acquired and lock_released are paired normal-path events.
   lock_abandoned is a separate category for ownership discontinuity - it is NOT
   a release. Abandoned locks always attempt canonical rollback before emitting.
   ========================================================= */

/** Emitted when a schema-wide regeneration lock is successfully acquired. */
| {
    type:             "regeneration:lock_acquired";
    projectId:        string;
    /** Phase 4A: always "schema". Sub-scopes deferred to Phase 4B. */
    scope:            "schema";
    holder:           string;
    ttlMs:            number;
    fingerprintBefore: string;
  }

/** Emitted on normal lock release (completed, rolled_back, expired, forced, persistence_restore). */
| {
    type:      "regeneration:lock_released";
    projectId: string;
    scope:     "schema";
    holder:    string;
    reason:    "completed" | "rolled_back" | "expired" | "forced" | "persistence_restore";
    durationMs: number;
  }

/**
 * Emitted when a lock is abandoned due to ownership discontinuity.
 * Distinct from lock_released - abandonment means the holder can no longer
 * complete or roll back. Rollback is attempted automatically.
 * Reasons: runtime_reload, persistence_restore, hmr, crash_recovery, process_restart
 */
| {
    type:      "regeneration:lock_abandoned";
    projectId: string;
    scope:     "schema";
    holder:    string;
    reason:    "runtime_reload" | "persistence_restore" | "hmr" | "crash_recovery" | "process_restart";
    durationMs: number;
  }

/**
 * Emitted by schemaBridge when a mutation is blocked because a regeneration lock
 * is active for the requested scope.
 */
| {
    type:            "regeneration:blocked";
    projectId:       string;
    scope:           "schema";
    requestedBy:     string;
    blockedByHolder: string;
    mutationType:    string;
  }

/** Emitted on successful lock release after regeneration completed. */
| {
    type:              "regeneration:completed";
    projectId:         string;
    scope:             "schema";
    holder:            string;
    fingerprintBefore: string;
    fingerprintAfter:  string;
    durationMs:        number;
    mutationsApplied:  number;
  }

/**
 * Emitted when a regeneration session is rolled back (failure, expiry, or forced).
 * canonicalRestored: true means the pre-acquisition canonical schema was restored.
 */
| {
    type:              "regeneration:rollback";
    projectId:         string;
    scope:             "schema";
    holder:            string;
    reason:            "failure" | "expired" | "forced";
    fingerprintBefore: string;
    canonicalRestored: boolean;
  }

/* =========================================================
   MUTATION QUEUE EVENTS  (Phase 4C-A)

   Emitted by schemaBridge.ts during mutation queue lifecycle.
   Provide observability for enqueue, drain, retry, expiry, and discard.

   GOVERNANCE RULE: queue_entry_discarded for user-origin entries always
   sets requiresUserNotification = true - UI layer must surface these
   without auto-dismiss. queue_batch_expired is used when multiple user
   entries expire or are flushed in the same cycle (Rule S18).

   ALL ten event types must be registered here before any queue logic
   is written - this is a binding implementation gate (Gate A5).
   ========================================================= */

| {
    type:        "mutation:queue_entry_enqueued";
    entryId:     string;
    origin:      "user" | "system";
    mutationType: string;
    targetLabel: string;
    enqueuedAt:  number;
    expiresAt:   number;
    queueDepth:  number;
  }

| {
    type:                 "mutation:queue_entry_expired";
    entryId:              string;
    origin:               "user" | "system";
    mutationType:         string;
    targetLabel:          string;
    enqueuedAt:           number;
    expiresAt:            number;
    discardedAt:          number;
    preemptionCount:      number;
    fingerprintAtEnqueue: string;
    fingerprintAtDiscard: string;
  }

| {
    type:        "mutation:queue_entry_applied";
    entryId:     string;
    origin:      "user" | "system";
    mutationType: string;
    targetLabel: string;
  }

| {
    type:        "mutation:queue_entry_retry";
    entryId:     string;
    origin:      "user" | "system";
    mutationType: string;
    targetLabel: string;
    retryCount:  number;
  }

| {
    type:        "mutation:queue_entry_execution_failed";
    entryId:     string;
    origin:      "user" | "system";
    mutationType: string;
    targetLabel: string;
    retryCount:  number;
    issues:      string[];
  }

| {
    type:                     "mutation:queue_entry_discarded";
    entryId:                  string;
    origin:                   "user" | "system";
    requiresUserNotification: boolean;
    description:              string;
    discardReason:            string;
  }

| {
    type:                     "mutation:queue_flushed";
    reason:                   string;
    userOriginCount:          number;
    systemOriginCount:        number;
    notificationPendingCount: number;
  }

| {
    type:           "mutation:drain_started";
    drainSessionId: string;
    queueDepth:     number;
  }

| {
    type:             "mutation:drain_completed";
    drainSessionId:   string;
    appliedCount:     number;
    expiredCount:     number;
    durationMs:       number;
  }

| {
    type:               "mutation:drain_interrupted";
    drainSessionId:     string;
    processedCount:     number;
    remainingCount:     number;
    interruptedByHolder: string;
  }

| {
    type:    "mutation:queue_batch_expired";
    entries: Array<{ entryId: string; description: string; mutationType: string }>;
    origin:  "user";
  }

/* =========================================================
   PHASE 3 - APP RUNTIME EVENTS

   Emitted by Phase 3 engines (runtime kernel, state engine,
   data engine, action engine). Consumed by diagnostics,
   observability, and replay systems.

   Prefix: "app:" - distinct from platform events above.
   No new imports required - all payload types are inline primitives.
   ========================================================= */

// App lifecycle

| { type: "app:booted";         projectId: string; mode: "preview" | "publish" | "export" | "debug" | "app" }
| { type: "app:teardown";       projectId: string }
| { type: "app:page_navigated"; projectId: string; pageId: string; route: string }

// App state

| { type: "app:state_changed";      projectId: string; stateId: string; key: string; scope: "global" | "page" | "component" }
| { type: "app:state_reset";        projectId: string; scope: "global" | "page" | "component"; pageId?: string; componentId?: string }
| { type: "app:snapshot_taken";     projectId: string; snapshotId: string }
| { type: "app:snapshot_restored";  projectId: string; snapshotId: string }

// App data

| { type: "app:record_inserted"; projectId: string; collection: string; recordId: string }
| { type: "app:record_updated";  projectId: string; collection: string; recordId: string }
| { type: "app:record_deleted";  projectId: string; collection: string; recordId: string }

// App actions

| { type: "app:action_executed";     projectId: string; actionId: string; actionType: string; durationMs: number }
| { type: "app:action_failed";       projectId: string; actionId: string; actionType: string; reason: string }
| { type: "app:workflow_started";    projectId: string; workflowId: string }
| { type: "app:workflow_completed";  projectId: string; workflowId: string; durationMs: number }
| { type: "app:workflow_failed";     projectId: string; workflowId: string; reason: string }

// App command events - express intent to an external subsystem.
// Emitter: actionExecutor (Layer 2C). Consumed by: routing runtime / UI renderer.
// Taxonomy: Command Events. Replay behavior: SUPPRESSED (Layer 2C replayEngine).
// ET-7: exactly one consumer permitted per event type (enforced by commandDispatcher).

| { type: "app:navigate";     projectId: string; route: string; replace: boolean }
| { type: "app:notification"; projectId: string; message: string; variant: "info" | "success" | "warning" | "error"; duration?: number }

| { type: "runtime:error-reported"; source: string; message: string; stack?: string; projectId?: string; timestamp: number }
| { type: "runtime:span-completed"; spanId: string; name: string; durationMs: number; parentId?: string; meta?: Record<string, unknown> }

/* =========================================================
   CODE RUNTIME EVENTS  (Phase 2)
   Emitted by the code editor when a founder saves or resets an in-place file edit.
   Consumed by RuntimeCodePreview to hot-reload the no-iframe runtime on save, so the
   preview runs the founder's edited code without a manual re-run.
   ========================================================= */
| { type: "code:saved"; projectId: string; path: string; content: string }
| { type: "code:reset"; projectId: string; path: string }

/* =========================================================
   PREVIEW RUNTIME + DEBUGGER EVENTS  (Phase 4)
   Emitted/consumed by previewRuntimeSandbox: the multi-file preview runtime,
   its hot-reload path, and the step debugger (breakpoints, scope, continue).
   Not command events (ET-7 does not apply): zero or many consumers are allowed.
   ========================================================= */
| { type: "preview:update";          bundle: string }
| { type: "preview:resume" }
| { type: "runtime:paused";          file: string; line: number; scope: RuntimeDebugScope }
| { type: "runtime:scope";           scope: RuntimeDebugScope }
| { type: "runtime:get-scope" }
| { type: "runtime:continue" }
| { type: "runtime:highlight-line";  generatedLine: number }
| { type: "editor:toggle-breakpoint"; projectId: string; file: string; line: number }
| { type: "editor:open-file";        file: string; line?: number }

/* =========================================================
   EXECUTION ENGINE EVENTS
   Emitted by executionEngine.ts during code compilation and preview.
   These events were previously cast as (eventBus as any) - now typed.
   ========================================================= */
| { type: "runtime:rerender";      componentId: string }
| { type: "preview:render";        projectId: string; render: unknown }
| { type: "preview:refresh";       componentId: string }
| { type: "execution:complete";    projectId: string; result: unknown }
| { type: "execution:error";       projectId: string; error: string }
| { type: "execution:logs";        projectId: string; logs: string[] }

/* =========================================================
   AUTH STATE EVENTS
   Emitted by executionEngine.ts auth layer on session changes.
   ========================================================= */
| { type: "auth:changed";          user: { id: string; email?: string; role?: string } | null; session: unknown }

/* =========================================================
   WORKSPACE EVENTS
   Emitted by executionEngine.ts workspace engine on workspace switch.
   ========================================================= */
| { type: "workspace:changed";     workspace: { id: string; name: string; owner_id: string } | null }

/* =========================================================
   COMMAND BUS EVENTS
   Emitted by the command bus on command lifecycle transitions.
   ========================================================= */
| { type: "command:dispatched";    commandId: string; commandType: string; source: string }
| { type: "command:completed";     commandId: string; commandType: string; durationMs: number }
| { type: "command:failed";        commandId: string; commandType: string; error: string }
| { type: "command:rejected";      commandId: string; commandType: string; reason: string }

/* =========================================================
   EDITOR ENGINE EVENTS
   Emitted by the editor engine on document lifecycle events.
   ========================================================= */
| { type: "editor:document_opened";   docId: string; path: string }
| { type: "editor:document_modified"; docId: string; path: string; version: number }
| { type: "editor:document_saved";    docId: string; path: string; version: number }
| { type: "editor:document_closed";   docId: string; path: string }
| { type: "editor:buffer_created";    docId: string; path: string; sizeBytes: number }

/* =========================================================
   PAGE MANAGER EVENTS
   Emitted by pageManager on navigation and lifecycle transitions.
   ========================================================= */
| { type: "page:manager_ready";       pageCount: number }
| { type: "page:activated";           pageId: string; route: string }
| { type: "page:deactivated";         pageId: string }
| { type: "page:hydrated";            pageId: string; route: string }

/* =========================================================
   RENDER COORDINATOR EVENTS
   Emitted by renderCoordinator on render lifecycle events.
   ========================================================= */
| { type: "render:frame_started";     frameId: string; pageId: string }
| { type: "render:frame_completed";   frameId: string; pageId: string; durationMs: number }
| { type: "render:frame_failed";      frameId: string; pageId: string; error: string }
| { type: "render:coordinator_ready" }

type EventType = NuvraEvent["type"];

type EventOfType<T extends EventType> = Extract<NuvraEvent, { type: T }>;

type Listener<T extends EventType> = (event: EventOfType<T>) => void;

export interface EventRecord {
event: NuvraEvent;
timestamp: number;
}

const MAX_HISTORY = 200;

class EventBus {
private listeners = new Map<EventType, Set<Listener<any>>>();
private history: EventRecord[] = [];
private globalListeners = new Set<(event: NuvraEvent) => void>();

/** Subscribe to a specific event type. Returns unsubscribe function. */
on<T extends EventType>(type: T, listener: Listener<T>): () => void {
if (!this.listeners.has(type)) {
  this.listeners.set(type, new Set());
}
this.listeners.get(type)!.add(listener);
return () => this.listeners.get(type)?.delete(listener);
}

/** Subscribe once - auto-removes after first call. */
once<T extends EventType>(type: T, listener: Listener<T>): () => void {
const wrapped: Listener<T> = (event) => {
  unsub();
  listener(event);
};
const unsub = this.on(type, wrapped);
return unsub;
}

/** Subscribe to ALL events. */
onAny(listener: (event: NuvraEvent) => void): () => void {
this.globalListeners.add(listener);
return () => this.globalListeners.delete(listener);
}

/** Emit an event to all subscribers. */
emit<T extends EventType>(event: EventOfType<T>): void {
const record: EventRecord = { event, timestamp: Date.now() };

this.history.push(record);
if (this.history.length > MAX_HISTORY) {
  this.history = this.history.slice(-MAX_HISTORY);
}

// Type-specific listeners
const listeners = this.listeners.get(event.type);
if (listeners) {
  for (const fn of listeners) {
    try {
      fn(event as any);
    } catch (err) {
      console.error(`[EventBus] Listener error on "${event.type}":`, err);
    }
  }
}

// Global listeners
for (const fn of this.globalListeners) {
  try {
    fn(event);
  } catch (err) {
    console.error(`[EventBus] Global listener error:`, err);
  }
}
}

/** Get recent event history. */
getHistory(filter?: EventType): EventRecord[] {
if (filter) return this.history.filter((r) => r.event.type === filter);
return [...this.history];
}

/** Clear all listeners (for shutdown). */
clear(): void {
this.listeners.clear();
this.globalListeners.clear();
}

/** Reset everything including history. */
reset(): void {
this.clear();
this.history = [];
}
}

export const eventBus = new EventBus();